import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { stampsAPI } from '../services/api';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import BadgeIcon from '@mui/icons-material/Badge';
import ReceiptIcon from '@mui/icons-material/Receipt';
import GavelIcon from '@mui/icons-material/Gavel';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import TableChartIcon from '@mui/icons-material/TableChart';
import './StampForm.css';

const EMPTY_INFO_ROW = { type: '', name: '', cnic: '', contact: '', use_on_form: '' };

function StampForm({ stamp, onClose }) {
  const [formData, setFormData] = useState({
    stamp_number: '',
    stamp_status: 'issued',
    total_paid_amount: '',
    challan_number: '',
    challan_type: 'Judicial',
    branch_code: '',
    payment_reference: '',
    payment_date: new Date().toISOString().split('T')[0],
    stamp_amount: '',
    stamp_sno: '',
    vendor_stamp: '',
    stamp_paper_type: 'Judicial',
    challan_amount_paid_by: '',
    deed_type: '',
    court_fee: '',
    district: '',
    tehsil: '',
    applicant_name: '',
    applicant_contact: '',
    applicant_cnic: '',
    applicant_email: '',
  });

  // Dynamic info rows — default 1 row
  const [infoRows, setInfoRows] = useState([
    { ...EMPTY_INFO_ROW },
  ]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (stamp) {
      const { id, unique_id, qr_code, created_at, updated_at, info_rows, ...rest } = stamp;

      // Remove legacy flat info fields from formData
      let i = 1;
      while (rest[`info${i}_name`] !== undefined) {
        delete rest[`info${i}_type`];
        delete rest[`info${i}_name`];
        delete rest[`info${i}_cnic`];
        delete rest[`info${i}_contact`];
        delete rest[`info${i}_use_on_form`];
        i++;
      }

      setFormData(rest);

      // Load info rows from JSON column if available, else fall back to legacy fields
      if (info_rows) {
        try {
          const parsed = typeof info_rows === 'string' ? JSON.parse(info_rows) : info_rows;
          setInfoRows(parsed.length > 0 ? parsed : [{ ...EMPTY_INFO_ROW }]);
        } catch {
          setInfoRows([{ ...EMPTY_INFO_ROW }]);
        }
      } else {
        // Legacy: reconstruct from info1_*, info2_*
        const rows = [];
        let j = 1;
        while (stamp[`info${j}_name`] !== undefined) {
          rows.push({
            type: stamp[`info${j}_type`] || '',
            name: stamp[`info${j}_name`] || '',
            cnic: stamp[`info${j}_cnic`] || '',
            contact: stamp[`info${j}_contact`] || '',
            use_on_form: stamp[`info${j}_use_on_form`] || '',
          });
          j++;
        }
        setInfoRows(rows.length > 0 ? rows : [{ ...EMPTY_INFO_ROW }]);
      }
    }
  }, [stamp]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInfoChange = (index, field, value) => {
    setInfoRows((prev) => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const addInfoRow = () => setInfoRows((prev) => [...prev, { ...EMPTY_INFO_ROW }]);
  const removeInfoRow = (index) => {
    if (infoRows.length === 1) return toast.warning('At least one information row is required');
    setInfoRows((prev) => prev.filter((_, i) => i !== index));
  };

  // Send info_rows as JSON array — backend handles flattening
  const buildPayload = () => {
    const payload = { ...formData };
    payload.info_rows = JSON.stringify(infoRows);
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = buildPayload();
      if (stamp) {
        await stampsAPI.update(stamp.id, payload);
        toast.success('Stamp updated successfully');
      } else {
        await stampsAPI.create(payload);
        toast.success('Stamp created successfully');
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving stamp');
    } finally {
      setLoading(false);
    }
  };

  const renderField = (fieldName, label, options = null, type = 'text') => {
    if (options) {
      return (
        <div key={fieldName} className="form-group">
          <label>{label}</label>
          <select name={fieldName} value={formData[fieldName]} onChange={handleChange}>
            <option value="">Select...</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      );
    }
    return (
      <div key={fieldName} className="form-group">
        <label>{label}</label>
        <input
          type={type}
          name={fieldName}
          value={formData[fieldName] || ''}
          onChange={handleChange}
          placeholder={label}
        />
      </div>
    );
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content stamp-form">
        <div className="modal-header">
          <h2>{stamp ? 'Edit Stamp' : 'Add New Stamp'}</h2>
          <button type="button" onClick={onClose} className="icon-btn-close"><CloseIcon /></button>
        </div>

        <form onSubmit={handleSubmit} className="form-container">

          {/* Stamp Details */}
          <div className="form-section">
            <h3><BadgeIcon className="section-icon" /> Stamp Details</h3>
            <div className="form-grid">
              {renderField('stamp_number', 'Stamp Number')}
              {renderField('stamp_status', 'Stamp Status', ['issued', 'verified', 'cancelled'])}
              {renderField('total_paid_amount', 'Total Paid Amount')}
            </div>
          </div>

          {/* Challan Information */}
          <div className="form-section">
            <h3><ReceiptIcon className="section-icon" /> Challan Information</h3>
            <div className="form-grid">
              {renderField('challan_number', 'Challan Number')}
              {renderField('challan_type', 'Challan Type', ['Judicial', 'Original'])}
              {renderField('branch_code', 'Branch Code')}
              {renderField('payment_reference', 'Payment Reference')}
              {renderField('payment_date', 'Payment Date', null, 'date')}
              {renderField('stamp_amount', 'Stamp Amount')}
              {renderField('stamp_sno', 'Stamp S.No')}
              {renderField('vendor_stamp', 'Vendor Stamp')}
            </div>
          </div>

          {/* Deed Detail */}
          <div className="form-section">
            <h3><GavelIcon className="section-icon" /> Deed Detail</h3>
            <div className="form-grid">
              {renderField('stamp_paper_type', 'Stamp Paper Type', ['Judicial', 'Original'])}
              {renderField('challan_amount_paid_by', 'Challan Amount Paid By')}
              {renderField('deed_type', 'Deed Type')}
              {renderField('court_fee', 'Court Fee')}
            </div>
          </div>

          {/* Location */}
          <div className="form-section">
            <h3><LocationOnIcon className="section-icon" /> Location Information</h3>
            <div className="form-grid">
              {renderField('district', 'District')}
              {renderField('tehsil', 'Tehsil')}
            </div>
          </div>

          {/* Applicant */}
          <div className="form-section">
            <h3><PersonIcon className="section-icon" /> Applicant Information</h3>
            <div className="form-grid">
              {renderField('applicant_name', 'Applicant Name')}
              {renderField('applicant_contact', 'Applicant Contact')}
              {renderField('applicant_cnic', 'Applicant CNIC')}
              {renderField('applicant_email', 'Applicant Email', null, 'email')}
            </div>
          </div>

          {/* Dynamic Information Table */}
          <div className="form-section">
            <div className="info-table-header">
              <h3><TableChartIcon className="section-icon" /> Information Table</h3>
              <button type="button" className="btn-add-row" onClick={addInfoRow}>
                <AddCircleIcon fontSize="small" /> Add Row
              </button>
            </div>

            <div className="info-cards-list">
              {infoRows.map((row, index) => (
                <div key={index} className="info-card">
                  <div className="info-card-header">
                    <span className="info-card-num">Entry {index + 1}</span>
                    <button
                      type="button"
                      className="btn-remove-row"
                      onClick={() => removeInfoRow(index)}
                      title="Remove entry"
                    >
                      <RemoveCircleIcon fontSize="small" />
                    </button>
                  </div>
                  <div className="info-card-grid">
                    <div className="form-group">
                      <label>Type</label>
                      <input
                        type="text"
                        value={row.type}
                        onChange={(e) => handleInfoChange(index, 'type', e.target.value)}
                        placeholder="e.g. Buyer, Seller"
                      />
                    </div>
                    <div className="form-group">
                      <label>Name / Company Name</label>
                      <input
                        type="text"
                        value={row.name}
                        onChange={(e) => handleInfoChange(index, 'name', e.target.value)}
                        placeholder="Full name"
                      />
                    </div>
                    <div className="form-group">
                      <label>CNIC / NTN</label>
                      <input
                        type="text"
                        value={row.cnic}
                        onChange={(e) => handleInfoChange(index, 'cnic', e.target.value)}
                        placeholder="CNIC or NTN"
                      />
                    </div>
                    <div className="form-group">
                      <label>Contact #</label>
                      <input
                        type="text"
                        value={row.contact}
                        onChange={(e) => handleInfoChange(index, 'contact', e.target.value)}
                        placeholder="Contact number"
                      />
                    </div>
                    <div className="form-group">
                      <label>Use on Form</label>
                      <input
                        type="text"
                        value={row.use_on_form}
                        onChange={(e) => handleInfoChange(index, 'use_on_form', e.target.value)}
                        placeholder="Yes / No"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <SaveIcon fontSize="small" /> {loading ? 'Saving...' : 'Save Stamp'}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary">
              <CloseIcon fontSize="small" /> Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default StampForm;
